import { NavLink } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  X,
  LayoutDashboard,
  Package,
  FolderOpen,
  FileText,
  Image,
  Award,
  Settings,
  MessageSquare,
  Mail,
  SlidersHorizontal,
  Filter,
  MessageCircle,
} from 'lucide-react'
import { cn } from '@/lib/utils'

const navItems = [
  { to: '/admin', icon: LayoutDashboard, label: 'Dashboard', end: true },
  { to: '/admin/products', icon: Package, label: 'Products' },
  { to: '/admin/categories', icon: FolderOpen, label: 'Categories' },
  { to: '/admin/filters', icon: Filter, label: 'Filters' },
  { to: '/admin/configurators', icon: SlidersHorizontal, label: 'Configurators' },
  { to: '/admin/quotes', icon: FileText, label: 'Quote Requests' },
  { to: '/admin/gallery', icon: Image, label: 'Gallery' },
  { to: '/admin/certificates', icon: Award, label: 'Certificates' },
  { to: '/admin/subscribers', icon: Mail, label: 'Subscribers' },
  { to: '/admin/messages', icon: MessageSquare, label: 'Messages' },
  { to: '/admin/live-chat', icon: MessageCircle, label: 'Live Chat' },
  { to: '/admin/settings', icon: Settings, label: 'Settings' },
]

interface AdminMobileNavProps {
  isOpen: boolean
  onClose: () => void
}

export function AdminMobileNav({ isOpen, onClose }: AdminMobileNavProps) {
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed left-0 top-0 bottom-0 w-72 bg-secondary-900 z-50 lg:hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-secondary-800">
              <div>
                <span className="text-xl font-bold text-primary-400">Engitronix</span>
                <span className="text-xs text-secondary-400 mt-1 block">Admin</span>
              </div>
              <button
                onClick={onClose}
                className="p-2 text-secondary-400 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Navigation */}
            <nav className="p-4 space-y-1">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  onClick={onClose}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors',
                      isActive
                        ? 'bg-primary-600 text-white'
                        : 'text-secondary-300 hover:bg-secondary-800 hover:text-white'
                    )
                  }
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </NavLink>
              ))}
            </nav>

            {/* Preview Links */}
            <div className="p-4 border-t border-secondary-800 mt-auto">
              <p className="text-xs text-secondary-500 uppercase tracking-wider mb-2">Preview Site</p>
              <div className="flex flex-wrap gap-2">
                <a href="/?preview=true" target="_blank" rel="noopener noreferrer" className="text-xs text-secondary-400 hover:text-white">Home</a>
                <span className="text-secondary-600">•</span>
                <a href="/products?preview=true" target="_blank" rel="noopener noreferrer" className="text-xs text-secondary-400 hover:text-white">Products</a>
                <span className="text-secondary-600">•</span>
                <a href="/about?preview=true" target="_blank" rel="noopener noreferrer" className="text-xs text-secondary-400 hover:text-white">About</a>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
